// Idle worker warm-up scheduler for heavy runtime sources.
// It overlaps worker bootstrap with other rebuild work when possible.
import type { BuildSourceEntry } from './types'
import { prewarmWorkerForPredictedEntries } from './prewarm-worker'

type IdleCallbackHandle = number
type IdleCallbackDeadline = {
  didTimeout: boolean
  timeRemaining: () => number
}

type IdleWindow = Window & typeof globalThis & {
  requestIdleCallback?: (callback: (deadline: IdleCallbackDeadline) => void, options?: { timeout?: number }) => IdleCallbackHandle
  cancelIdleCallback?: (handle: IdleCallbackHandle) => void
  __LentyStyleWorkerWarmupPromise?: Promise<void> | null
}

let pendingIdleHandle: IdleCallbackHandle | null = null

// Schedule non-blocking worker warm-up when current entries predict worker compile.
export function scheduleWorkerWarmup(entries: BuildSourceEntry[]): void {
  const runtimeWindow = resolveIdleWindow()
  // Skip scheduling when browser runtime is not available.
  if (!runtimeWindow) {
    return
  } else {
    // Continue worker warm-up scheduling in browser runtime.
  }

  cancelPendingIdleWarmup(runtimeWindow)
  const startWarmup = () => {
    runtimeWindow.__LentyStyleWorkerWarmupPromise = prewarmWorkerForPredictedEntries(entries)
      .catch(() => undefined)
      .finally(() => {
        runtimeWindow.__LentyStyleWorkerWarmupPromise = null
      })
  }

  // Prefer idle time so warm-up does not block the active rebuild path.
  if (typeof runtimeWindow.requestIdleCallback === 'function') {
    pendingIdleHandle = runtimeWindow.requestIdleCallback(() => {
      pendingIdleHandle = null
      startWarmup()
    }, { timeout: 50 })
  } else {
    // Fallback to macrotask scheduling when idle callback is unavailable.
    pendingIdleHandle = window.setTimeout(() => {
      pendingIdleHandle = null
      startWarmup()
    }, 0)
  }
}

// Read active warm-up promise started by a previous rebuild cycle.
export function getScheduledWorkerWarmupPromise(): Promise<void> | null {
  const runtimeWindow = resolveIdleWindow()
  // Return null when browser runtime is not available.
  if (!runtimeWindow) {
    return null
  } else {
    // Continue scheduled warm-up promise lookup.
  }

  // Return in-flight worker warm-up promise when one exists.
  return runtimeWindow.__LentyStyleWorkerWarmupPromise ?? null
}

// Resolve browser window with optional idle callback helpers.
function resolveIdleWindow(): IdleWindow | null {
  // Return null outside browser runtime.
  if (typeof window === 'undefined') {
    return null
  } else {
    // Continue browser idle scheduling path.
  }

  // Return typed browser window for warm-up state tracking.
  return window as IdleWindow
}

// Cancel stale idle warm-up callback before scheduling a newer one.
function cancelPendingIdleWarmup(runtimeWindow: IdleWindow): void {
  // Skip cancellation when no idle warm-up is pending.
  if (pendingIdleHandle === null) {
    return
  } else {
    // Continue stale idle warm-up cancellation.
  }

  if (typeof runtimeWindow.cancelIdleCallback === 'function') {
    runtimeWindow.cancelIdleCallback(pendingIdleHandle)
  } else {
    window.clearTimeout(pendingIdleHandle)
  }
  pendingIdleHandle = null
}
